import React from "react";
import { Plus, Trash2, X } from "lucide-react";
import { Pill, Label, inputCls } from "./Bits";

const APPETITES = [
  { key: "small", label: "Smaller" },
  { key: "medium", label: "Standard" },
  { key: "large", label: "Big eater" },
  { key: "very_large", label: "Very big" },
];
const PET_TYPES = ["dog", "cat", "bird", "fish", "other"];
export const TASTES = [
  { key: "simple", label: "Simple & classic", hint: "Roasts, pasta bakes, meat & three veg — familiar and mild." },
  { key: "middle", label: "Middle of the road", hint: "Favourites, plus the odd new dish." },
  { key: "adventurous", label: "Adventurous", hint: "Bold spice, global flavours, new ingredients." },
];

export const newMember = (role = "adult") => ({ name: "", role, age: role === "child" ? 6 : null, appetite: role === "child" ? "small" : "medium", fussy_level: 2, dislikes: [], taste: "" });

export function PeopleStep({ members, setMembers }) {
  const adults = members.filter((m) => m.role !== "child");
  const kids = members.filter((m) => m.role === "child");
  const update = (m, key, val) => setMembers(members.map((x) => (x === m ? { ...x, [key]: val } : x)));
  const remove = (m) => setMembers(members.filter((x) => x !== m));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <Counter label="Adults" value={adults.length} testId="adults-count"
          onAdd={() => setMembers([...members, newMember("adult")])}
          onRemove={() => adults.length > 1 && remove(adults[adults.length - 1])} />
        <Counter label="Children" value={kids.length} testId="children-count"
          onAdd={() => setMembers([...members, newMember("child")])}
          onRemove={() => kids.length > 0 && remove(kids[kids.length - 1])} />
      </div>

      {members.map((m, i) => (
        <div key={i} className="bg-white rounded-3xl border-2 border-[#FFF3F0] p-4 shadow-soft" data-testid={`member-card-${i}`}>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${m.role === "child" ? "bg-sorted-sage" : "bg-sorted-blush"} text-sorted-ink`}>{m.role === "child" ? "Child" : "Adult"}</span>
            <input data-testid={`member-name-${i}`} value={m.name} onChange={(e) => update(m, "name", e.target.value)} placeholder="Name"
              className="flex-1 min-w-0 bg-muted rounded-2xl px-3 py-2.5 font-semibold text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach" />
            {m.role === "child" && (
              <input type="number" min="0" max="17" data-testid={`member-age-${i}`} value={m.age ?? ""} onChange={(e) => update(m, "age", e.target.value === "" ? null : Number(e.target.value))}
                placeholder="Age" className="w-16 bg-muted rounded-2xl px-3 py-2.5 font-semibold text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach text-center" />
            )}
            {members.length > 1 && (
              <button type="button" onClick={() => remove(m)} data-testid={`remove-member-${i}`} aria-label="Remove member"
                className="w-9 h-9 rounded-full bg-muted text-sorted-slate flex items-center justify-center hover:bg-sorted-blush shrink-0">
                <Trash2 size={16} strokeWidth={2.5} />
              </button>
            )}
          </div>

          <p className="text-xs font-bold text-sorted-slate mt-3 mb-1.5">Appetite</p>
          <div className="flex flex-wrap gap-2">
            {APPETITES.map((a) => (
              <Pill key={a.key} active={m.appetite === a.key} onClick={() => update(m, "appetite", a.key)} testId={`appetite-${i}-${a.key}`}>{a.label}</Pill>
            ))}
          </div>

          <p className="text-xs font-bold text-sorted-slate mt-3 mb-1.5">Fussy eater: {m.fussy_level}/5</p>
          <input type="range" min="1" max="5" value={m.fussy_level} data-testid={`fussy-${i}`}
            onChange={(e) => update(m, "fussy_level", Number(e.target.value))} className="w-full accent-[#FFB5A7]" />

          <p className="text-xs font-bold text-sorted-slate mt-3 mb-1.5">Taste</p>
          <div className="flex flex-wrap gap-2" data-testid={`taste-${i}`}>
            <Pill active={!m.taste} onClick={() => update(m, "taste", "")} testId={`taste-${i}-default`}>Same as household</Pill>
            {TASTES.map((t) => (
              <Pill key={t.key} active={m.taste === t.key} onClick={() => update(m, "taste", t.key)} testId={`taste-${i}-${t.key}`}>{t.label}</Pill>
            ))}
          </div>

          <MemberDislikes m={m} i={i} update={update} />
        </div>
      ))}
    </div>
  );
}

function MemberDislikes({ m, i, update }) {
  const [text, setText] = React.useState("");
  const add = () => {
    const v = text.trim();
    if (!v) return;
    update(m, "dislikes", [...(m.dislikes || []), v]);
    setText("");
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2 items-center">
      {(m.dislikes || []).map((d, di) => (
        <span key={di} className="bg-sorted-blush text-sorted-ink text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
          {d}<button type="button" onClick={() => update(m, "dislikes", m.dislikes.filter((_, x) => x !== di))}><X size={12} strokeWidth={3} /></button>
        </span>
      ))}
      <input data-testid={`dislike-input-${i}`} value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="+ won't eat"
        className="w-28 bg-muted rounded-full px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-sorted-peach" />
    </div>
  );
}

function Counter({ label, value, onAdd, onRemove, testId }) {
  return (
    <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] p-3 flex items-center justify-between shadow-soft" data-testid={testId}>
      <span className="text-sm font-bold text-sorted-ink">{label}</span>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onRemove} data-testid={`${testId}-minus`} aria-label={`Fewer ${label}`} className="w-8 h-8 rounded-full bg-muted text-sorted-ink font-bold">−</button>
        <span className="font-heading font-bold text-lg w-5 text-center" data-testid={`${testId}-value`}>{value}</span>
        <button type="button" onClick={onAdd} data-testid={`${testId}-plus`} aria-label={`More ${label}`} className="w-8 h-8 rounded-full bg-sorted-peach text-sorted-ink font-bold">+</button>
      </div>
    </div>
  );
}

export function PetsStep({ pets, setPets }) {
  const update = (i, key, val) => setPets(pets.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));
  return (
    <div className="space-y-4">
      <Label hint="I'll pop their food on the shopping list and keep unsafe leftovers away from them.">Any furry (or feathered) family?</Label>
      {pets.map((p, i) => (
        <div key={i} className="bg-white rounded-3xl border-2 border-[#FFF3F0] p-4 shadow-soft space-y-3" data-testid={`pet-card-${i}`}>
          <div className="flex items-center gap-2">
            <input data-testid={`pet-name-${i}`} value={p.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Pet's name" className={inputCls} />
            <button type="button" onClick={() => setPets(pets.filter((_, x) => x !== i))} data-testid={`remove-pet-${i}`} aria-label="Remove pet"
              className="w-9 h-9 rounded-full bg-muted text-sorted-slate flex items-center justify-center shrink-0"><Trash2 size={16} strokeWidth={2.5} /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            {PET_TYPES.map((t) => <Pill key={t} active={p.type === t} onClick={() => update(i, "type", t)} testId={`pet-type-${i}-${t}`}>{t}</Pill>)}
          </div>
          <input data-testid={`pet-food-${i}`} value={p.food} onChange={(e) => update(i, "food", e.target.value)} placeholder="Their food, e.g. Dry dog food 3kg" className={`${inputCls} w-full`} />
        </div>
      ))}
      <button type="button" onClick={() => setPets([...pets, { type: "dog", name: "", food: "" }])} data-testid="add-pet"
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-sorted-blush text-sorted-ink font-bold py-3 rounded-2xl hover:bg-muted transition-colors">
        <Plus size={18} strokeWidth={2.5} /> Add a pet
      </button>
    </div>
  );
}
